/**
 * Stripe Dev Mode Utilities
 * Provides mock Stripe functionality for local development without real credentials.
 */

// Test card numbers and their expected behaviors
export const TEST_CARDS = {
  '4242424242424242': 'success',
  '4000000000000002': 'decline',
  '4000000000009995': 'insufficient',
};

// Human-readable error messages for test behaviors
export const BEHAVIOR_MESSAGES = {
  success: null,
  decline: 'Your card was declined.',
  insufficient: 'Your card has insufficient funds.',
};

/**
 * Check if Stripe dev mode bypass is enabled.
 * Returns true if REACT_APP_STRIPE_DEV_BYPASS is 'true'.
 */
export const isStripeDevMode = () => {
  return process.env.REACT_APP_STRIPE_DEV_BYPASS === 'true';
};

/**
 * Get the behavior for a card number.
 * @param {string} cardNumber - The card number (spaces/dashes will be removed)
 * @returns {string} - The behavior: 'success', 'decline', or 'insufficient'
 */
export const getCardBehavior = (cardNumber) => {
  const normalized = (cardNumber || '').replace(/[\s-]/g, '');
  return TEST_CARDS[normalized] || 'success';
};

/**
 * Generate a fake payment method ID based on card number behavior.
 * Format: pm_dev_{behavior}_{timestamp}
 * @param {string} cardNumber - The card number
 * @returns {string} - A fake payment method ID
 */
export const generatePaymentMethodId = (cardNumber) => {
  const behavior = getCardBehavior(cardNumber);
  return `pm_dev_${behavior}_${Date.now()}`;
};

/**
 * Validate a card number using the Luhn algorithm.
 * @param {string} cardNumber - The card number to validate
 * @returns {boolean} - True if valid according to Luhn algorithm
 */
export const validateCardNumber = (cardNumber) => {
  const normalized = (cardNumber || '').replace(/[\s-]/g, '');

  if (!/^\d{13,19}$/.test(normalized)) {
    return false;
  }

  // Luhn algorithm
  let sum = 0;
  let isEven = false;

  for (let i = normalized.length - 1; i >= 0; i--) {
    let digit = parseInt(normalized[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Validate expiry date (MM/YY format).
 * @param {string} expiry - The expiry string
 * @returns {boolean} - True if valid and not expired
 */
export const validateExpiry = (expiry) => {
  const match = (expiry || '').match(/^(\d{2})\/(\d{2})$/);
  if (!match) return false;

  const month = parseInt(match[1], 10);
  const year = parseInt(match[2], 10) + 2000;

  if (month < 1 || month > 12) return false;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (year < currentYear) return false;
  if (year === currentYear && month < currentMonth) return false;

  return true;
};

/**
 * Validate CVC (3-4 digits).
 * @param {string} cvc - The CVC string
 * @returns {boolean} - True if valid format
 */
export const validateCvc = (cvc) => {
  return /^\d{3,4}$/.test(cvc || '');
};

/**
 * Simulate Stripe's confirmCardSetup behavior in dev mode.
 * @param {object} cardData - The card data from DevCardElement
 * @returns {object} - A mock setupIntent result
 */
export const mockConfirmCardSetup = (cardData) => {
  const behavior = getCardBehavior(cardData.number);

  if (behavior === 'decline') {
    return {
      error: { message: BEHAVIOR_MESSAGES.decline },
      setupIntent: null,
    };
  }

  if (behavior === 'insufficient') {
    return {
      error: { message: BEHAVIOR_MESSAGES.insufficient },
      setupIntent: null,
    };
  }

  return {
    error: null,
    setupIntent: {
      payment_method: generatePaymentMethodId(cardData.number),
      status: 'succeeded',
    },
  };
};
